/**
 * User-Friendly Error Fallback UI Components
 *
 * These components provide contextual error displays for different
 * types of failures (forms, media, async loading, etc.)
 */

import React from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Home,
  Copy,
  Check,
  Video,
  VideoOff,
  FileX,
  WifiOff,
  FormInput,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import { useThemeStore } from '@/stores/themeStore';

// Base props for all fallback UIs
export interface ErrorFallbackProps {
  error: Error | null;
  errorInfo?: React.ErrorInfo | null;
  onRetry?: () => void;
  onGoHome?: () => void;
  onGoBack?: () => void;
  onReportError?: () => void;
  retryCount?: number;
  maxRetries?: number;
  isRetrying?: boolean;
  title?: string;
  message?: string;
  showDetails?: boolean;
  showReportButton?: boolean;
}

/**
 * Base Error Fallback - Generic error display
 */
export const BaseErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorInfo,
  onRetry,
  onGoHome,
  onGoBack,
  onReportError,
  retryCount = 0,
  maxRetries = 3,
  isRetrying = false,
  title = 'Something went wrong',
  message = 'An unexpected error has occurred. Please try again.',
  showDetails = process.env.NODE_ENV === 'development',
  showReportButton = true,
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const [copied, setCopied] = React.useState(false);

  const canRetry = retryCount < maxRetries;

  const handleCopyError = async () => {
    if (!error) return;

    const errorText = JSON.stringify(
      {
        name: error.name,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo?.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
      null,
      2
    );

    try {
      await navigator.clipboard.writeText(errorText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback: create downloadable file
      const blob = new Blob([errorText], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `error-report-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const containerClass = isDark
    ? 'mm-workplace mm-workplace-bg text-white'
    : 'mm-light mm-light-bg text-gray-900';

  const cardClass = isDark
    ? 'mm-glass'
    : 'bg-white border border-gray-200 shadow-lg';

  const iconBgClass = isDark
    ? 'bg-rose-500/15 border border-rose-500/30'
    : 'bg-rose-50 border border-rose-200';

  return (
    <div className={`min-h-[300px] flex items-center justify-center p-4 ${containerClass}`}>
      <div className={`max-w-lg w-full rounded-2xl p-6 ${cardClass}`}>
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${iconBgClass}`}>
            <AlertTriangle className={`w-7 h-7 ${isDark ? 'text-rose-300' : 'text-rose-500'}`} />
          </div>
        </div>

        {/* Title & Message */}
        <h2 className="text-xl font-semibold text-center mb-2">{title}</h2>
        <p className={`text-center mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {message}
        </p>

        {/* Retry Status */}
        {isRetrying && (
          <div className={`mb-4 p-3 rounded-xl ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
            <p className={`text-sm text-center ${isDark ? 'text-amber-200' : 'text-amber-700'}`}>
              Retrying... (Attempt {retryCount + 1}/{maxRetries})
            </p>
          </div>
        )}

        {/* Error Details (Development) */}
        {showDetails && error && (
          <div className={`mb-4 p-3 rounded-xl text-xs font-mono overflow-auto max-h-40 ${isDark ? 'bg-black/30 border border-white/10' : 'bg-gray-100 border border-gray-200'}`}>
            <p className={`font-semibold mb-1 ${isDark ? 'text-rose-300' : 'text-rose-600'}`}>
              {error.name}: {error.message}
            </p>
            {error.stack && (
              <details className={isDark ? 'text-white/60' : 'text-gray-500'}>
                <summary className="cursor-pointer">Stack trace</summary>
                <pre className="whitespace-pre-wrap mt-1">{error.stack}</pre>
              </details>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 justify-center">
          {onRetry && canRetry && (
            <button
              onClick={onRetry}
              disabled={isRetrying}
              className="mm-btn mm-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
              Try Again
            </button>
          )}

          {onGoBack && (
            <button
              onClick={onGoBack}
              className="mm-btn mm-btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          )}

          {onGoHome && (
            <button
              onClick={onGoHome}
              className="mm-btn mm-btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm"
            >
              <Home className="w-4 h-4" />
              Home
            </button>
          )}

          {showReportButton && onReportError && (
            <button
              onClick={handleCopyError}
              className="mm-btn mm-btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Report
                </>
              )}
            </button>
          )}
        </div>

        {/* Help Text */}
        <p className={`text-xs text-center mt-4 ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
};

/**
 * Video Error Fallback - For video player/recorder failures
 */
export const VideoErrorFallback: React.FC<ErrorFallbackProps & {
  isRecording?: boolean;
  videoSrc?: string;
}> = ({
  error,
  onRetry,
  isRecording = false,
  ...props
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const isMediaError = error?.name === 'NotAllowedError' || error?.name === 'NotFoundError';
  const isPermissionError = error?.name === 'NotAllowedError';

  const iconBgClass = isDark
    ? 'bg-violet-500/15 border border-violet-500/30'
    : 'bg-violet-50 border border-violet-200';

  const cardClass = isDark
    ? 'mm-glass'
    : 'bg-white border border-gray-200 shadow-lg';

  let title = 'Video Error';
  let message = 'There was a problem with the video.';

  if (isPermissionError) {
    title = 'Camera Access Denied';
    message = 'Please allow camera access in your browser settings to continue.';
  } else if (error?.name === 'NotFoundError') {
    title = 'No Camera Found';
    message = 'Please connect a camera and try again.';
  } else if (isRecording) {
    title = 'Recording Error';
    message = 'There was a problem with the recording. Your progress may have been saved.';
  }

  return (
    <div className={`aspect-video flex items-center justify-center rounded-2xl ${cardClass}`}>
      <div className="text-center p-6">
        <div className="flex justify-center mb-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${iconBgClass}`}>
            {isMediaError ? (
              <VideoOff className={`w-7 h-7 ${isDark ? 'text-violet-300' : 'text-violet-500'}`} />
            ) : (
              <Video className={`w-7 h-7 ${isDark ? 'text-violet-300' : 'text-violet-500'}`} />
            )}
          </div>
        </div>

        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {props.title || title}
        </h3>
        <p className={`text-sm mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {props.message || message}
        </p>

        {onRetry && (
          <button
            onClick={onRetry}
            className="mm-btn mm-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        )}

        {isPermissionError && (
          <p className={`text-xs mt-3 ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
            Check your browser's address bar for camera permission settings.
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Form Error Fallback - For form submission failures
 */
export const FormErrorFallback: React.FC<ErrorFallbackProps & {
  formName?: string;
  onDismiss?: () => void;
}> = ({
  error,
  onRetry,
  onDismiss,
  formName = 'Form',
  ...props
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const iconBgClass = isDark
    ? 'bg-amber-500/15 border border-amber-500/30'
    : 'bg-amber-50 border border-amber-200';

  return (
    <div className={`p-4 rounded-xl ${isDark ? 'bg-rose-500/10 border border-rose-500/30' : 'bg-rose-50 border border-rose-200'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center ${iconBgClass}`}>
          <FormInput className={`w-5 h-5 ${isDark ? 'text-amber-300' : 'text-amber-600'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {props.title || `${formName} Error`}
          </h4>
          <p className={`text-sm mt-1 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            {props.message || error?.message || 'There was a problem submitting the form. Please try again.'}
          </p>

          <div className="flex gap-2 mt-3">
            {onRetry && (
              <button
                onClick={onRetry}
                className="mm-btn mm-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            )}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="mm-btn mm-btn-secondary inline-flex items-center px-3 py-1.5 text-sm"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Async/Loading Error Fallback - For async data loading failures
 */
export const AsyncErrorFallback: React.FC<ErrorFallbackProps & {
  resourceName?: string;
}> = ({
  error,
  onRetry,
  resourceName = 'content',
  retryCount = 0,
  maxRetries = 3,
  isRetrying = false,
  ...props
}) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const isNetworkError = error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('fetch');
  const isTimeout = error?.message?.toLowerCase().includes('timeout');

  const canRetry = retryCount < maxRetries;

  const iconBgClass = isDark
    ? 'bg-blue-500/15 border border-blue-500/30'
    : 'bg-blue-50 border border-blue-200';

  const cardClass = isDark
    ? 'mm-glass'
    : 'bg-white border border-gray-200 shadow-lg';

  let title = `Failed to load ${resourceName}`;
  let message = 'There was a problem loading the content.';

  if (isNetworkError) {
    title = 'Connection Error';
    message = 'Please check your internet connection and try again.';
  } else if (isTimeout) {
    title = 'Request Timeout';
    message = 'The request took too long. Please try again.';
  }

  return (
    <div className={`p-6 rounded-2xl ${cardClass}`}>
      <div className="flex flex-col items-center text-center">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${iconBgClass}`}>
          {isNetworkError ? (
            <WifiOff className={`w-6 h-6 ${isDark ? 'text-blue-300' : 'text-blue-500'}`} />
          ) : (
            <FileX className={`w-6 h-6 ${isDark ? 'text-blue-300' : 'text-blue-500'}`} />
          )}
        </div>

        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          {props.title || title}
        </h3>
        <p className={`text-sm mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
          {props.message || message}
        </p>

        {isRetrying && (
          <p className={`text-xs mb-3 ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
            Retrying... ({retryCount + 1}/{maxRetries})
          </p>
        )}

        {onRetry && canRetry && (
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="mm-btn mm-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Retrying...' : 'Try Again'}
          </button>
        )}

        {!canRetry && (
          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-400'}`}>
            Maximum retry attempts reached. Please refresh the page.
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Inline Error Alert - For minor errors that don't block the UI
 */
export const InlineErrorAlert: React.FC<{
  error: Error | string | null;
  onDismiss?: () => void;
  onRetry?: () => void;
  className?: string;
}> = ({ error, onDismiss, onRetry, className = '' }) => {
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  if (!error) return null;

  const message = typeof error === 'string' ? error : error.message;

  return (
    <div
      className={`flex items-center gap-2 p-3 rounded-xl ${
        isDark
          ? 'bg-rose-500/10 border border-rose-500/30'
          : 'bg-rose-50 border border-rose-200'
      } ${className}`}
      role="alert"
    >
      <AlertCircle className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-rose-300' : 'text-rose-500'}`} />
      <p className={`flex-1 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className={`p-1.5 rounded-lg hover:bg-white/10 ${isDark ? 'text-white/70' : 'text-gray-600'}`}
          aria-label="Retry"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      )}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className={`p-1.5 rounded-lg hover:bg-white/10 ${isDark ? 'text-white/70' : 'text-gray-600'}`}
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * Page Error Fallback - For full page errors
 */
export const PageErrorFallback: React.FC<ErrorFallbackProps> = (props) => {
  return (
    <div className="min-h-screen">
      <BaseErrorFallback {...props} />
    </div>
  );
};

export default BaseErrorFallback;
